import { useEffect, useState } from 'react';
import {
  checkBrowserSupport,
  pickLinkedWorkbook,
  queryFolderPermission,
  requestFolderPermission,
  restoreLinkTarget,
  writeTextFile,
  readWorkbookBytes,
  type LinkTarget,
  type PermissionState,
} from '@/giraffe/linkFsAccess';
import {
  FOLDER_CHOSEN_MESSAGE,
  WRITE_REQUEST_MESSAGE,
  WRITE_RESULT_MESSAGE,
  type FolderChosenMessage,
  type WriteRequestMessage,
  type WriteResultMessage,
} from '@/giraffe/linkPopup';

/**
 * The link service window — ported from giraffe-excel-poc's
 * FolderPickerPopup.tsx. Runs at `?popup=link` (see main.tsx), in its own
 * top-level browsing context: the pickers it uses, and every file write after
 * them, are blocked by Chrome inside gTables' own cross-origin iframe.
 *
 * Stays open and relays writes for the rest of the session — see
 * linkFsAccess.ts's own header for exactly why a write can't just happen once
 * here and be done with it.
 */
export function LinkPopupPage(): JSX.Element {
  const support = checkBrowserSupport();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [target, setTarget] = useState<LinkTarget | null>(null);
  const [pending, setPending] = useState<LinkTarget | null>(null);
  const [restoring, setRestoring] = useState(true);
  const [busy, setBusy] = useState(false);

  function announce(confirmed: LinkTarget): void {
    setTarget(confirmed);
    setPending(null);
    const msg: FolderChosenMessage = { type: FOLDER_CHOSEN_MESSAGE, workbookFileName: confirmed.workbookFileName };
    window.opener?.postMessage(msg, window.location.origin);
  }

  useEffect(() => {
    if (!support.supported) return;
    void (async () => {
      const restored = await restoreLinkTarget();
      if (!restored) {
        setRestoring(false);
        return;
      }
      const permission: PermissionState = await queryFolderPermission(restored.folderHandle);
      if (permission === 'granted') {
        announce(restored);
        setMessage(`"${restored.workbookFileName}" already has write access. Keep this window open.`);
      } else {
        setPending(restored);
        setMessage(`Remembered "${restored.workbookFileName}" from a previous session — click below to re-confirm write access.`);
      }
      setRestoring(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [support.supported]);

  async function handlePick(): Promise<void> {
    setMessage(null);
    setError(false);
    setBusy(true);
    try {
      const picked = await pickLinkedWorkbook();
      announce(picked);
      setMessage(`"${picked.workbookFileName}" linked. Keep this window open — it relays every write back to gTables.`);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(true);
      setMessage(`Could not link that file: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleRegrant(): Promise<void> {
    if (!pending) return;
    setMessage(null);
    setError(false);
    setBusy(true);
    try {
      const result = await requestFolderPermission(pending.folderHandle);
      if (result === 'granted') {
        announce(pending);
        setMessage(`Write access to "${pending.workbookFileName}"'s folder re-confirmed. Keep this window open.`);
      } else {
        setError(true);
        setMessage(`Permission was not granted (${result}). Link a file again instead.`);
      }
    } catch (err) {
      setError(true);
      setMessage(`Could not re-request permission: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!target) return undefined;
    function handleMessage(event: MessageEvent<WriteRequestMessage>): void {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== WRITE_REQUEST_MESSAGE) return;
      if (!target) return;
      const { requestId, action } = event.data;
      const reply = (result: Omit<WriteResultMessage, 'type' | 'requestId'>): void => {
        const msg: WriteResultMessage = { type: WRITE_RESULT_MESSAGE, requestId, ...result };
        window.opener?.postMessage(msg, window.location.origin);
      };
      void (async () => {
        try {
          if (action.kind === 'writeJson') {
            await writeTextFile(target.folderHandle, action.fileName, action.content);
            setMessage(`Wrote ${action.fileName}.`);
            reply({ ok: true });
          } else {
            const bytes = await readWorkbookBytes(target.folderHandle, target.workbookFileName);
            reply({ ok: true, bytes });
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          setError(true);
          setMessage(`Request failed: ${msg}`);
          reply({ ok: false, error: msg });
        }
      })();
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [target]);

  if (!support.supported) {
    return (
      <main className="gt-popup">
        <p className="gt-popup-error">{support.reason}</p>
      </main>
    );
  }

  return (
    <main className="gt-popup">
      <h1>Link a gTables workbook</h1>
      <p>
        This runs in its own window because the file pickers — and every file write after them — are
        blocked by Chrome inside gTables' embedded app panel.
      </p>
      {message ? <p className={error ? 'gt-popup-error' : 'gt-popup-status'}>{message}</p> : null}
      {!restoring ? (
        <div className="gt-popup-actions">
          {pending && !target ? (
            <button className="gt-btn" onClick={() => void handleRegrant()} disabled={busy}>
              Re-grant access to "{pending.workbookFileName}"
            </button>
          ) : null}
          <button className="gt-btn gt-btn-primary" onClick={() => void handlePick()} disabled={busy || !!target}>
            Choose the linked workbook…
          </button>
          {target ? (
            <button className="gt-btn" onClick={() => window.close()}>
              Close window
            </button>
          ) : null}
        </div>
      ) : null}
      {target ? (
        <p className="gt-popup-status">
          Leave this window open in the background. Go back to gTables to keep working — it will write
          through this window on its own.
        </p>
      ) : null}
    </main>
  );
}
